import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from './client';
import type { PaginatedResponse, QueryParams } from '@/types/api';
import type {
  Task,
  TaskActivityItem,
  TaskAttachmentItem,
  TaskChecklistItem,
  TaskWatcherItem,
} from '@/types/models';
import { taskKeys } from '@/constants/queryKeys';

type RawTaskComment = {
  id: string;
  authorName: string;
  content: string;
  createdAt: string;
};

type RawTask = {
  id: string;
  title: string;
  notes?: string | null;
  relatedProject?: string | null;
  relatedProjectId?: string | null;
  relatedClient?: string | null;
  assignedMemberId?: string | null;
  assignedMember?: { id?: string; name?: string } | null;
  assignedManagerId?: string | null;
  assignedManager?: { id?: string; name?: string } | null;
  assignedBranch?: string | null;
  status?: string | null;
  approvalStatus?: string | null;
  priority?: string | null;
  deadline?: string | null;
  deadlineState?: Task['deadlineState'];
  category?: string | null;
  startDate?: string | null;
  estimatedHours?: number | null;
  createdAt: string;
  updatedAt?: string | null;
  lastActivityAt?: string | null;
  progress?: number | null;
  progressUpdatedAt?: string | null;
  actualHours?: number | null;
  taskAttachments?: unknown;
  checklistItems?: TaskChecklistItem[];
  checklist?: TaskChecklistItem[];
  checklistTotal?: number | null;
  checklistDone?: number | null;
  activity?: TaskActivityItem[];
  activityCount?: number | null;
  watchers?: TaskWatcherItem[];
  watcherCount?: number | null;
  blockedReason?: string | null;
  blockedAt?: string | null;
  comments?: RawTaskComment[];
  managerApprovedDate?: string | null;
  managerApprovalNote?: string | null;
  ceoVerifiedDate?: string | null;
  completedDate?: string | null;
};

export type TaskSummary = {
  total: number;
  notStarted: number;
  inProgress: number;
  blocked: number;
  review: number;
  done: number;
  overdue: number;
  dueToday: number;
  waitingApproval: number;
  submitted: number;
  approved: number;
  verified: number;
};

function parseAttachments(raw: unknown): TaskAttachmentItem[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: TaskAttachmentItem[] = [];
  for (const x of raw) {
    if (x && typeof x === 'object' && 'url' in x && 'name' in x) {
      out.push({
        url: String((x as TaskAttachmentItem).url),
        name: String((x as TaskAttachmentItem).name),
        uploadedAt: 'uploadedAt' in x ? String((x as TaskAttachmentItem).uploadedAt) : '',
      });
    }
  }
  return out.length ? out : undefined;
}

function computeDeadlineState(raw: RawTask): Task['deadlineState'] {
  if (raw.deadlineState) return raw.deadlineState;
  if (!raw.deadline) return 'none';
  if (raw.status === 'DONE') return 'done';
  const due = new Date(raw.deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  if (due < today) return 'overdue';
  if (due <= end) return 'today';
  return 'upcoming';
}

function mapTask(raw: RawTask): Task {
  const checklist = raw.checklistItems ?? raw.checklist ?? undefined;
  const checklistDone =
    raw.checklistDone ?? checklist?.filter((item) => item.isDone).length ?? undefined;
  const checklistTotal = raw.checklistTotal ?? checklist?.length ?? undefined;
  return {
    id: raw.id,
    title: raw.title,
    description: raw.notes ?? '',
    projectId: raw.relatedProjectId ?? undefined,
    projectName: raw.relatedProject ?? undefined,
    relatedProjectId: raw.relatedProjectId ?? undefined,
    relatedClient: raw.relatedClient ?? undefined,
    assigneeId: raw.assignedMemberId ?? raw.assignedMember?.id ?? '',
    assigneeName: raw.assignedMember?.name ?? 'Unassigned',
    assignedBranch: raw.assignedBranch,
    assignedManagerId: raw.assignedManagerId,
    assignedManagerName: raw.assignedManager?.name,
    status: (raw.status ?? 'NOT_STARTED') as Task['status'],
    approvalStatus: (raw.approvalStatus ?? 'WAITING') as Task['approvalStatus'],
    priority: (raw.priority ?? 'MEDIUM') as Task['priority'],
    dueDate: raw.deadline ?? raw.createdAt,
    startDate: raw.startDate ?? undefined,
    estimatedHours: raw.estimatedHours ?? undefined,
    deadlineState: computeDeadlineState(raw),
    tags: raw.category ? [raw.category] : [],
    createdAt: raw.createdAt,
    lastActivityAt: raw.lastActivityAt ?? raw.updatedAt ?? raw.createdAt,
    progress: raw.progress ?? undefined,
    progressUpdatedAt: raw.progressUpdatedAt ?? undefined,
    actualHours: raw.actualHours ?? undefined,
    taskAttachments: parseAttachments(raw.taskAttachments),
    checklist,
    checklistDone,
    checklistTotal,
    activity: raw.activity,
    activityCount: raw.activityCount ?? raw.activity?.length,
    watchers: raw.watchers,
    watcherCount: raw.watcherCount ?? raw.watchers?.length,
    blockedReason: raw.blockedReason ?? undefined,
    blockedAt: raw.blockedAt ?? undefined,
    managerApprovedDate: raw.managerApprovedDate ?? undefined,
    managerApprovalNote: raw.managerApprovalNote ?? undefined,
    ceoVerifiedDate: raw.ceoVerifiedDate ?? undefined,
    completedDate: raw.completedDate ?? undefined,
    ...(raw.comments ? { comments: raw.comments } : {}),
  } as Task;
}

type UseTasksOptions = {
  enabled?: boolean;
};

function invalidateTaskQueries(qc: ReturnType<typeof useQueryClient>, taskId?: string) {
  qc.invalidateQueries({ queryKey: taskKeys.root });
  qc.invalidateQueries({ queryKey: taskKeys.my });
  if (taskId) qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
}

export function useTasks(params?: QueryParams, options?: UseTasksOptions) {
  return useQuery<PaginatedResponse<Task>>({
    queryKey: taskKeys.list(params),
    queryFn: async () => {
      const { data } = await api.get('/tasks', { params });
      const rows = Array.isArray(data?.data) ? data.data : [];
      return { ...data, data: rows.map((item: RawTask) => mapTask(item)) };
    },
    enabled: options?.enabled !== false,
  });
}

export function useTaskSummary(params?: QueryParams, options?: UseTasksOptions) {
  return useQuery<TaskSummary>({
    queryKey: taskKeys.summary(params),
    queryFn: async () => {
      const { data } = await api.get('/tasks/summary', { params });
      return data as TaskSummary;
    },
    enabled: options?.enabled !== false,
  });
}

export function useTask(id: string) {
  return useQuery<Task>({
    queryKey: taskKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get(`/tasks/${id}`);
      return mapTask(data as RawTask);
    },
    enabled: !!id,
  });
}

export function useTaskActivity(id: string) {
  return useQuery<TaskActivityItem[]>({
    queryKey: taskKeys.activity(id),
    queryFn: async () => {
      const { data } = await api.get(`/tasks/${id}/activity`);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id,
  });
}

export type TaskMeta = {
  categories: string[];
  branches: { id: string; name: string; city: string | null }[];
};

export function useTaskMeta(options?: { enabled?: boolean }) {
  return useQuery<TaskMeta>({
    queryKey: [...taskKeys.root, 'meta'],
    queryFn: async () => {
      const { data } = await api.get('/tasks/meta');
      return data as TaskMeta;
    },
    enabled: options?.enabled !== false,
  });
}

export function useMyTasks(params?: QueryParams) {
  return useQuery<Task[]>({
    queryKey: [...taskKeys.my, params],
    queryFn: async () => {
      const { data } = await api.get('/tasks', { params: { mine: true, limit: 100, ...params } });
      const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return rows.map((item: RawTask) => mapTask(item));
    },
  });
}

export type CreateTaskInput = {
  title: string;
  priority?: string;
  notes?: string;
  deadline?: string;
  startDate?: string;
  estimatedHours?: number;
  category?: string;
  assignedBranch?: string;
  assignedManagerId?: string;
  assignedMemberId?: string;
  relatedProjectId?: string;
  relatedClient?: string;
  checklistItems?: string[];
};

export type UpdateTaskInput = { id: string } & Partial<
  Omit<CreateTaskInput, 'assignedMemberId' | 'checklistItems'> & {
    progress?: number;
    actualHours?: number;
    status?: 'NOT_STARTED' | 'IN_PROGRESS';
    assignedMemberId?: string | null;
  }
>;

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTaskInput) => {
      const res = await api.post('/tasks', body);
      return mapTask(res.data as RawTask);
    },
    onSuccess: (task) => invalidateTaskQueries(qc, task.id),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateTaskInput) => api.put(`/tasks/${id}`, body),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: taskKeys.detail(id) });
      invalidateTaskQueries(qc);
    },
  });
}

export function useUploadTaskAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, files }: { taskId: string; files: File[] }) => {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      const { data } = await api.post(`/tasks/${taskId}/attachments`, form);
      return data;
    },
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.taskId),
  });
}

export function useAddTaskChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) =>
      api.post(`/tasks/${taskId}/checklist`, { title }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.taskId),
  });
}

export function useUpdateTaskChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, itemId, ...body }: { taskId: string; itemId: string; title?: string; isDone?: boolean }) =>
      api.patch(`/tasks/${taskId}/checklist/${itemId}`, body),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.taskId),
  });
}

export function useDeleteTaskChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, itemId }: { taskId: string; itemId: string }) =>
      api.delete(`/tasks/${taskId}/checklist/${itemId}`),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.taskId),
  });
}

export function useWatchTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.post(`/tasks/${taskId}/watch`),
    onSuccess: (_, taskId) => invalidateTaskQueries(qc, taskId),
  });
}

export function useUnwatchTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.delete(`/tasks/${taskId}/watch`),
    onSuccess: (_, taskId) => invalidateTaskQueries(qc, taskId),
  });
}

export function useBlockTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/tasks/${id}/block`, { reason }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useUnblockTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/unblock`),
    onSuccess: (_, id) => invalidateTaskQueries(qc, id),
  });
}

export function useSubmitTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note, actualHours, progress }: { id: string; note?: string; actualHours?: number; progress?: number }) =>
      api.post(`/tasks/${id}/submit`, { note, actualHours, progress }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useApproveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => api.post(`/tasks/${id}/approve`, { note }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useRejectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => api.post(`/tasks/${id}/reject`, { note }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useVerifyTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/verify`),
    onSuccess: (_, id) => invalidateTaskQueries(qc, id),
  });
}

export function useRateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rating, note }: { id: string; rating: number; note?: string }) =>
      api.post(`/tasks/${id}/rate`, { rating, note }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.id),
  });
}

export function useAddTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, content }: { taskId: string; content: string }) =>
      api.post(`/tasks/${taskId}/comments`, { content }),
    onSuccess: (_, v) => invalidateTaskQueries(qc, v.taskId),
  });
}
